import React from "react";
import ResponsiveECharts from "./ResponsiveECharts.jsx";
import { ALEA_INFO } from "../../constants.js";

// Même logique que dans utils/gantt.js : texte blanc sur fond foncé, noir
// sur fond clair.
function luminance(hex) {
  const clean = hex.replace("#", "");
  const v = clean.match(/\w\w/g).map((h) => parseInt(h, 16));
  return (0.299 * v[0] + 0.587 * v[1] + 0.114 * v[2]) / 255;
}

export default function PertesChart({ data, risque }) {
  const { total, repartition } = data;
  const totalSorted = [...total].sort((a, b) => a.YearGroup - b.YearGroup);
  const years = totalSorted.map((d) => d.YearGroup);

  const aleaCodes = Object.keys(ALEA_INFO);

  // Rayon proportionnel à la valeur totale de l'année (racine carrée, pas
  // linéaire, pour que ce soit la SURFACE du camembert qui soit
  // proportionnelle, pas juste son rayon) — le plus grand total obtient 95%,
  // les autres sont mis à l'échelle en conséquence.
  const maxTotal = Math.max(...totalSorted.map((d) => d.val), 0);
  const rayonPour = (val) => {
    if (maxTotal <= 0) return "70%";
    const ratio = Math.max(0, val) / maxTotal;
    return `${Math.round(95 * Math.sqrt(ratio))}%`;
  };

  const lineOption = {
    tooltip: { trigger: "axis" },
    grid: { left: 60, right: 20, top: 20, bottom: 40, containLabel: true },
    xAxis: { type: "category", data: years, boundaryGap: false, axisLabel: { fontSize: 14, formatter: (v) => String(v) } },
    yAxis: { type: "value", name: `Perte ${risque} (%)`, nameLocation: "middle", nameGap: 45 },
    series: [
      {
        name: "Total",
        type: "line",
        symbol: "circle",
        symbolSize: 10,
        lineStyle: { width: 2.5, color: "#c9a227" },
        itemStyle: { color: "#c9a227" },
        data: totalSorted.map((d) => d.val),
      },
    ],
  };

  const pieOptionFor = (year) => {
    const rows = repartition.filter((r) => r.YearGroup === year);
    const totalVal = totalSorted.find((d) => d.YearGroup === year)?.val ?? 0;
    return {
      series: [
        {
          type: "pie",
          radius: rayonPour(totalVal),
          center: ["50%", "55%"],
          label: {
            show: true,
            position: "inside",
            fontSize: 12,
            fontWeight: "bold",
            formatter: (params) => (params.percent > 0 ? `${params.percent.toFixed(1)}%` : ""),
          },
          labelLine: { show: false },
          data: aleaCodes.map((code) => {
            const color = ALEA_INFO[code].color;
            return {
              name: ALEA_INFO[code].label,
              value: rows.find((r) => r.alea === code)?.val ?? 0,
              itemStyle: { color },
              label: { color: luminance(color) > 0.6 ? "#111" : "#fff" },
            };
          }),
        },
      ],
    };
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start", width: "100%" }}>
      <div style={{ flex: "2 1 320px", minWidth: 280 }}>
        <ResponsiveECharts option={lineOption} style={{ height: 400 }} />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, flex: "3 1 480px" }}>
        {years.map((year) => (
          <div key={year} style={{ flex: "1 1 200px", minWidth: 180, textAlign: "center" }}>
            <ResponsiveECharts option={pieOptionFor(year)} style={{ height: 220 }} />
            <div style={{ fontWeight: "bold", marginTop: 4 }}>{year}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, width: "100%", justifyContent: "center" }}>
        {aleaCodes.map((code) => (
          <div key={code} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                background: ALEA_INFO[code].color,
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            <span style={{ fontSize: 13 }}>{ALEA_INFO[code].label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}